import React from 'react';
import { motion } from 'framer-motion';
import FormField from './FormField';
import ColorPalettePicker from './ColorPalettePicker';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { 
  compositions, 
  visualStyles, 
  lightingStyles, 
  environments, 
  audioDescriptions,
  cameraMovements,
  aiModels
} from '../data/presets';

const { FiClock, FiAlertCircle } = FiIcons;

const PromptForm = ({ formData, updateField, updateCustomField }) => {
  // Approximate speaking rate: 2.5 words per second
  const wordsPerSecond = 2.5;
  const maxWords = Math.floor((formData.duration || 8) * wordsPerSecond);
  
  const dialogueWords = formData.dialogue ? formData.dialogue.trim().split(/\s+/).length : 0;
  const isDialogueTooLong = dialogueWords > maxWords && formData.dialogue.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className="clay-card p-8"
    >
      <h2 className="text-xl font-bold neon-text mb-8">✨ Prompt Builder</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <FormField
          label="🤖 Target AI Model"
          type="select"
          value={formData.targetModel}
          onChange={(value) => updateField('targetModel', value)}
          placeholder="Select AI Model..."
          options={aiModels}
        />

        <div className="mb-6">
          <label className="block text-white font-medium mb-3 flex items-center gap-2">
            <SafeIcon icon={FiClock} className="text-green-400" />
            Video Duration
          </label>
          <div className="flex bg-black/20 p-1 rounded-xl clay-inner-shadow">
            {[8, 10, 15].map((seconds) => (
              <button
                key={seconds}
                onClick={() => updateField('duration', seconds)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  formData.duration === seconds 
                    ? 'bg-green-500 text-black shadow-lg' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {seconds}s
              </button>
            ))}
          </div>
        </div>
      </div>

      <FormField
        label="🎬 Describe Your Scene"
        value={formData.sceneDescription}
        onChange={(value) => updateField('sceneDescription', value)}
        placeholder="Enter scene description..."
        isTextarea={true}
      />

      <FormField
        label="📸 Composition"
        type="select"
        value={formData.composition}
        onChange={(value) => updateField('composition', value)}
        placeholder="Select composition..."
        options={compositions}
      />

      <FormField
        label="🎥 Camera Motion"
        type="select"
        value={formData.cameraMovement}
        onChange={(value) => updateField('cameraMovement', value)}
        placeholder="Select camera movement..."
        options={cameraMovements}
        hasCustomInput={true}
        customValue={formData.customCameraMovement}
        onCustomChange={(value) => updateCustomField('customCameraMovement', value)}
        customPlaceholder="Or enter your own camera movement..."
      />

      <FormField
        label="🎨 Style"
        type="select"
        value={formData.visualStyle}
        onChange={(value) => updateField('visualStyle', value)}
        placeholder="Select visual style..."
        options={visualStyles}
        hasCustomInput={true}
        customValue={formData.customVisualStyle}
        onCustomChange={(value) => updateCustomField('customVisualStyle', value)}
        customPlaceholder="Or enter your own visual style..."
      />

      <FormField
        label="💡 Lighting Style"
        type="select"
        value={formData.lighting}
        onChange={(value) => updateField('lighting', value)}
        placeholder="Select lighting..."
        options={lightingStyles}
        hasCustomInput={true}
        customValue={formData.customLighting}
        onCustomChange={(value) => updateCustomField('customLighting', value)}
        customPlaceholder="Or enter your own lighting style..."
      />

      <FormField
        label="🖼️ Background Setting"
        type="select"
        value={formData.environment}
        onChange={(value) => updateField('environment', value)}
        placeholder="Select environment..."
        options={environments}
        hasCustomInput={true}
        customValue={formData.customEnvironment}
        onCustomChange={(value) => updateCustomField('customEnvironment', value)}
        customPlaceholder="Or enter your own background setting..."
      />

      <FormField
        label="🔊 Audio Description"
        type="select"
        value={formData.audio}
        onChange={(value) => updateField('audio', value)}
        placeholder="Select audio..."
        options={audioDescriptions}
        hasCustomInput={true}
        customValue={formData.customAudio}
        onCustomChange={(value) => updateCustomField('customAudio', value)}
        customPlaceholder="Or enter your own audio description..."
      />

      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-white font-medium">
            💬 Spoken Text (optional)
          </label>
          <span className={`text-xs px-2 py-1 rounded-full ${isDialogueTooLong ? 'bg-red-500/20 text-red-400' : 'bg-gray-800 text-gray-400'}`}>
            {dialogueWords} / ~{maxWords} words for {formData.duration}s
          </span>
        </div>
        <textarea 
          value={formData.dialogue}
          onChange={(e) => updateField('dialogue', e.target.value)}
          placeholder="Enter dialogue..."
          rows="3"
          className={`w-full py-3 px-4 clay-input text-white highlight-hover ${isDialogueTooLong ? 'border-red-500/50 focus:ring-red-500' : ''}`}
        />
        {isDialogueTooLong && (
          <div className="flex items-center gap-2 mt-2 text-red-400 text-xs animate-pulse">
            <SafeIcon icon={FiAlertCircle} />
            <span>Text may be too long for {formData.duration}s video. Try shortening it.</span>
          </div>
        )}
      </div>

      <ColorPalettePicker
        selectedPalette={formData.selectedColorPalette}
        onPaletteChange={(palette) => updateField('selectedColorPalette', palette)}
      />
    </motion.div>
  );
};

export default PromptForm;